import { readFile, readdir, stat } from 'node:fs/promises'
import { join } from 'node:path'
import { parse } from 'yaml'

export type PackageKind = 'Application' | 'ExecutionEngine' | 'Bundle'

export interface ManifestMetadata {
  name: string
  'display-name'?: string
  version: string
  description?: string
  icon?: string
  author?: string
}

export interface PackagingSection {
  publisher?: string
  name?: string
  files?: string[]
  exclude?: string[]
  encryption?: 'none' | 'optional' | 'required'
  bundle?: { 'default-path'?: string }
}

export interface Manifest {
  'api-version'?: string
  kind: string
  metadata: ManifestMetadata
  packaging?: PackagingSection
  [key: string]: unknown
}

const KIND_MAP: Record<string, PackageKind> = {
  application: 'Application',
  executionengine: 'ExecutionEngine',
  bundle: 'Bundle',
}

export function normalizeKind(kind: string): PackageKind {
  const mapped = KIND_MAP[kind.replace(/[^a-z]/gi, '').toLowerCase()]
  if (!mapped) throw new Error(`Unsupported manifest kind '${kind}' — expected Application, ExecutionEngine or Bundle.`)
  return mapped
}

export async function loadManifest(path: string): Promise<Manifest> {
  const text = await readFile(path, 'utf8')
  const manifest = parse(text) as Manifest
  if (!manifest || typeof manifest !== 'object') throw new Error(`Failed to parse manifest at ${path}.`)
  if (!manifest.kind) throw new Error(`Manifest at ${path} is missing 'kind'.`)
  if (!manifest.metadata?.name) throw new Error(`Manifest at ${path} is missing 'metadata.name'.`)
  if (!manifest.metadata?.version) throw new Error(`Manifest at ${path} is missing 'metadata.version'.`)
  normalizeKind(manifest.kind)
  return manifest
}

/** Locates the manifest in a project directory: manifest.yaml or a single *.app.yaml / *.engine.yaml / *.bundle.yaml. */
export async function findManifest(dir: string): Promise<string> {
  const explicit = join(dir, 'manifest.yaml')
  try {
    if ((await stat(explicit)).isFile()) return explicit
  } catch {
    // fall through to pattern search
  }

  const entries = await readdir(dir)
  const candidates = entries.filter((name) => /\.(app|engine|bundle)\.ya?ml$/.test(name))
  if (candidates.length === 1) return join(dir, candidates[0]!)
  if (candidates.length === 0) {
    throw new Error(`No manifest found in ${dir}. Expected manifest.yaml or *.app.yaml / *.engine.yaml / *.bundle.yaml.`)
  }
  throw new Error(`Multiple manifests found in ${dir} (${candidates.join(', ')}). Pass --manifest to disambiguate.`)
}

export const NAME_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/

export function validatePackageName(name: string, label: string): void {
  if (!NAME_PATTERN.test(name)) {
    throw new Error(`Invalid ${label} '${name}' — use lowercase letters, digits and hyphens (no leading/trailing hyphen).`)
  }
}
